import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Loader, Toast } from '@deque/cauldron-react';

import PageTitle from '~/common/components/PageTitle';

import { useAuthContext } from '~/common/contexts/auth';
import { useEnterprises } from '~/common/contexts/enterprises';
import useTeam from '../hooks/useTeam';
import { EditVariant } from '../types';

import { type Team } from '~/common/api-client';
import billingClient, {
  type EnterpriseMembers
} from '~/common/utils/billing-client/client-v2';

import useV2EnterpriseMembers from '~/common/hooks/useV2EnterpriseMembers';
import NotFound from '~/common/pages/NotFound';
import globalStyles from '../app.css';
import TeamSettings from '../containers/TeamSettings';
import {
  splitUserIdsAndEmails,
  updatePendingUserTeamAccess,
  UpdatePendingUserTeamAccessAction
} from '../utils/teams-utils';
import { addUsersByIdOrEmail } from './CreateTeam';

interface RouteParams {
  id: string;
}

export const removeUsersByIdOrEmail = async ({
  token,
  enterpriseId,
  teamId,
  userIdsOrEmails,
  enterpriseMembers
}: {
  token: string;
  enterpriseId: string;
  teamId: string;
  userIdsOrEmails: string[];
  enterpriseMembers: EnterpriseMembers.PendingOrAcceptedMember[];
}) => {
  if (!userIdsOrEmails.length) {
    return;
  }

  const { keycloakIds, invitedEmails } = splitUserIdsAndEmails(userIdsOrEmails);

  if (keycloakIds.length) {
    await billingClient.teamMemberships.delete({
      token,
      enterpriseId,
      id: teamId,
      keycloakIds
    });
  }

  if (invitedEmails.length) {
    await updatePendingUserTeamAccess({
      token,
      enterpriseId,
      invitedEmails,
      teamId,
      enterpriseMembers,
      actionType: UpdatePendingUserTeamAccessAction.Remove
    });
  }
};

const EditTeam: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const { activeEnterprise } = useEnterprises();
  const { id: teamId } = useParams<RouteParams>();

  // There will always be a user with a token.
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const token = user!.token;
  // TODO: When multi-enterprise is supported, we should fetch the appropriate enterprise_id
  // There should always be an active enterprise because of <Protected isEnterpriseAdmin>
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const enterpriseId = activeEnterprise!.id;

  const {
    team: initialTeam,
    loading,
    error
  } = useTeam({ token, teamId, enterpriseId });

  const { pendingOrAcceptedMembers } = useV2EnterpriseMembers({
    token,
    enterpriseId
  });

  if (loading) {
    return (
      <>
        <PageTitle title={t('Edit Team')} />
        <Loader tabIndex={-1} label={t('Loading team')} />
      </>
    );
  }
  if (!initialTeam) {
    return (
      <>
        <PageTitle title={t('Edit Team')} />
        <NotFound />
      </>
    );
  }

  const submit = async (team: Team) => {
    await billingClient.teams.update({
      token,
      enterpriseId,
      id: team.id || teamId,
      name: team.name
    });

    await addUsersByIdOrEmail({
      token,
      enterpriseId,
      teamId: team.id || teamId,
      userIdsOrEmails: team.user_ids_to_add,
      enterpriseMembers: pendingOrAcceptedMembers ?? []
    });

    await removeUsersByIdOrEmail({
      token,
      enterpriseId,
      teamId: team.id || teamId,
      userIdsOrEmails: team.user_ids_to_remove,
      enterpriseMembers: pendingOrAcceptedMembers ?? []
    });
  };

  return (
    <div className={globalStyles.wrap}>
      <PageTitle title={t('Edit Team')} />
      {error && (
        <Toast show type="error">
          {error?.message}
        </Toast>
      )}
      <TeamSettings
        variant={EditVariant.edit}
        initialTeam={initialTeam}
        submit={submit}
      />
    </div>
  );
};

export default EditTeam;
